import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';
import { Spinner } from './Spinner';

/**
 * The primary action button.
 *
 * Note the `primary` variant: orange background with INK BLACK text, not white.
 * White on #FF5326 measures 3.2:1 and fails WCAG AA; Ink Black reaches 4.5:1 and
 * passes. This is not a stylistic choice — see docs/03-phase-1-foundation.md §5.4.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-sm font-medium select-none',
    'transition-colors duration-[--duration-micro] ease-[--ease-out-quint]',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-text-on-primary hover:bg-primary-hover active:bg-primary-active',
        secondary:
          'bg-surface-raised text-text-primary border border-border-default hover:bg-surface-hover active:bg-surface-active',
        ghost: 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
        subtle: 'bg-surface-sunken text-text-primary hover:bg-surface-hover',
        // Same reasoning as `primary` above, inverted for dark mode: white on the
        // dark-mode danger fill is only 3.42:1, so the text token flips with the theme.
        danger: 'bg-danger text-text-on-danger hover:bg-danger-hover',
        link: 'text-text-link underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 text-sm',
        lg: 'h-11 px-6 text-base',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps
  extends ComponentPropsWithoutRef<'button'>, VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a <button>, e.g. to wrap a router Link. */
  asChild?: boolean;
  isLoading?: boolean;
  /** Replaces the label while loading. Falls back to the existing children. */
  loadingText?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    fullWidth,
    asChild = false,
    isLoading = false,
    loadingText,
    leadingIcon,
    trailingIcon,
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = cn(buttonVariants({ variant, size, fullWidth }), className);

  // Slot requires exactly one child, so composed buttons opt out of the
  // loading/icon affordances rather than silently rendering something broken.
  if (asChild) {
    return (
      <Slot className={classes} ref={ref} {...rest}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled === true || isLoading}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {isLoading ? <Spinner size="xs" label={null} /> : leadingIcon}
      {isLoading && loadingText ? loadingText : children}
      {!isLoading && trailingIcon}
    </button>
  );
});

export { buttonVariants };
